import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/site-footer";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/parent-auth")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Accesso genitori — Lumen" },
      {
        name: "description",
        content:
          "Area riservata ai genitori: accedi con il tuo account per seguire compiti, obiettivi e progressi dei tuoi figli.",
      },
      { property: "og:title", content: "Accesso genitori — Lumen" },
      { property: "og:description", content: "Segui compiti e progressi dei tuoi figli con un account genitore." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,follow" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/parent-auth` }],
  }),
  component: ParentAuthPage,
});

function ParentAuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/family" });
    });
  }, [navigate]);

  async function markParent() {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    await supabase
      .from("profiles")
      .update({ account_type: "parent", account_roles: ["parent"], default_role: "parent", is_adult: true })
      .eq("id", u.user.id);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/family`, data: { full_name: fullName } },
        });
        if (error) throw error;
        if (!data.session) {
          toast.success("Ti abbiamo inviato un'email: conferma l'indirizzo per entrare.");
          setMode("signin");
          return;
        }
        await markParent();
        navigate({ to: "/family" });
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: "/family" });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Qualcosa è andato storto");
    } finally {
      setLoading(false);
    }
  }

  async function forgotPassword() {
    if (!email.trim()) {
      toast.error("Inserisci prima la tua email");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success("Email inviata: controlla la posta");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Qualcosa è andato storto");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background px-5 py-8">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[var(--shadow-soft)]">
            <Heart className="h-7 w-7" aria-hidden />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Accesso genitori</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Entra con il tuo account: vedrai compiti, obiettivi e progressi dei tuoi figli senza usare il loro profilo.
          </p>
        </div>

        <Card className="card-soft border-0 p-6">
          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="p-name">Nome e cognome</Label>
                <Input id="p-name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="p-email">Email</Label>
              <Input
                id="p-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-pw">Password</Label>
              <Input
                id="p-pw"
                type="password"
                minLength={6}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={loading}>
              {loading ? "Un attimo…" : mode === "signup" ? "Crea account genitore" : "Accedi"}
            </Button>
          </form>

          {mode === "signin" && (
            <button
              type="button"
              onClick={forgotPassword}
              disabled={loading}
              className="mt-3 block w-full text-center text-sm text-primary hover:underline"
            >
              Password dimenticata?
            </button>
          )}

          <button
            type="button"
            onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
            className="mt-4 block w-full text-center text-sm text-muted-foreground hover:text-foreground"
          >
            {mode === "signup" ? "Ho già un account" : "Non ho ancora un account genitore"}
          </button>

          <Link to="/auth" className="mt-4 block text-center text-sm text-muted-foreground hover:text-foreground">
            Sei uno studente o un docente? Vai all'accesso principale
          </Link>
        </Card>
      </div>
      <SiteFooter />
    </div>
  );
}
